import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { CookieService } from 'ngx-cookie-service';
import { HttpHeaders } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { firstValueFrom } from 'rxjs';
import { getEvaluaciones } from '../../riesgo-liquidez/query';
import { getDetalleIndicadores } from '../indicadores-financieros/querys';
import { getAhorros, getAsientos, getCuentasProm, getDetalleCuenta, getIndicadores, getInversion1, getLiquidez, getVariable, getVarProm, getVarTemp, getVarTempProm } from './query';

@Injectable({
  providedIn: 'root'
})
export class IndicadoresCoberturaService {
  private headers = new HttpHeaders().append('Authorization', 'Bearer ' + localStorage.getItem('token'));

  constructor(
    private apollo: Apollo,
    private cookieService: CookieService
  ) { }

  /**
   * Obtiene las evaluaciones por plantilla
   */
  async getEvaluaciones(idPlantilla: number) {
    try {
      const { data } = await firstValueFrom(
        this.apollo.use(environment.api_alertaTemprana).watchQuery<any>({
          query: getEvaluaciones,
          context: { headers: this.headers },
          variables: { idPlantilla: idPlantilla }
        }).valueChanges
      );
      return data;
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  /**
   * Obtiene los detalles de indicadores
   */
  async getDetalleIndicadores(idEvaluacion: number) {
    try {
      const { data } = await firstValueFrom(
        this.apollo.use(environment.api_alertaTemprana).watchQuery<any>({
          query: getDetalleIndicadores,
          context: { headers: this.headers },
          variables: { idEvaluacion: idEvaluacion }
        }).valueChanges
      );
      if (!data) throw new Error('No data received for evaluation ' + idEvaluacion);
      return data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  /**
   * Obtiene los indicadores
   */
  async getIndicadores(idPlantilla: number) {
    try {
      const { data } = await firstValueFrom(
        this.apollo.use(environment.api_alertaTemprana).watchQuery<any>({
          query: getIndicadores,
          context: { headers: this.headers },
          variables: { idPlantilla: idPlantilla }
        }).valueChanges
      );
      return data;
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  /**
   * Obtiene cuentas promedio múltiples
   */
  async getCuentasProm(variables: any) {
    try {
      const { data } = await firstValueFrom(
        this.apollo.use(environment.api_alertaTemprana).watchQuery<any>({
          query: getCuentasProm,
          context: { headers: this.headers },
          variables: variables
        }).valueChanges
      );
      return data;
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  /**
   * Obtiene variables temporales promedio
   */
  async getVarTempProm(variables: any) {
    try {
      const { data } = await firstValueFrom(
        this.apollo.use(environment.api_alertaTemprana).watchQuery<any>({
          query: getVarTempProm,
          context: { headers: this.headers },
          variables: variables
        }).valueChanges
      );
      return data;
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  /**
   * Obtiene variables promedio
   */
  async getVarProm(variables: any) {
    try {
      const { data } = await firstValueFrom(
        this.apollo.use(environment.api_alertaTemprana).watchQuery<any>({
          query: getVarProm,
          context: { headers: this.headers },
          variables: variables
        }).valueChanges
      );
      return data;
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  /**
   * Obtiene inversiones
   */
  async getInversiones(periodo: string) {
    try {
      const { data } = await firstValueFrom(
        this.apollo.use(environment.api_aleemaRiesgos).watchQuery<any>({
          query: getInversion1,
          context: { headers: this.headers },
          variables: { periodo: periodo }
        }).valueChanges
      );
      return data;
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  /**
   * Obtiene asientos
   */
  async getAsientos(periodo: string) {
    try {
      const { data } = await firstValueFrom(
        this.apollo.use(environment.api_aleemaRiesgos).watchQuery<any>({
          query: getAsientos,
          context: { headers: this.headers },
          variables: { periodo: periodo }
        }).valueChanges
      );
      return data;
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  /**
   * Obtiene liquidez
   */
  async getLiquidez(periodo: string) {
    try {
      const { data } = await firstValueFrom(
        this.apollo.use(environment.api_aleemaRiesgos).watchQuery<any>({
          query: getLiquidez,
          context: { headers: this.headers },
          variables: { periodo: periodo }
        }).valueChanges.source
      );
      return data;
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  /**
   * Obtiene ahorros
   */
  async getAhorros(periodo: string) {
    try {
      const { data } = await firstValueFrom(
        this.apollo.use(environment.api_aleemaRiesgos).watchQuery<any>({
          query: getAhorros,
          context: { headers: this.headers },
          variables: { periodo: periodo }
        }).valueChanges.source
      );
      return data;
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  /**
   * Obtiene detalle de cuenta
   */
  async getDetalleCuenta(idEvaluacion: number, idCuenta: string) {
    try {
      const { data } = await firstValueFrom(
        this.apollo.use(environment.api_alertaTemprana).watchQuery<any>({
          query: getDetalleCuenta,
          context: { headers: this.headers },
          variables: { idEvaluacion: idEvaluacion, idCuenta: idCuenta }
        }).valueChanges
      );
      return data;
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  /**
   * Obtiene variable
   */
  async getVariable(idEvaluacion: number, idVariable: string) {
    try {
      const { data } = await firstValueFrom(
        this.apollo.use(environment.api_alertaTemprana).watchQuery<any>({
          query: getVariable,
          context: { headers: this.headers },
          variables: { idEvaluacion: idEvaluacion, idVariable: idVariable }
        }).valueChanges
      );
      return data;
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  /**
   * Obtiene variable temporal
   */
  async getVarTemp(idEvaluacion: number, idVarTemp: string) {
    try {
      const { data } = await firstValueFrom(
        this.apollo.use(environment.api_alertaTemprana).watchQuery<any>({
          query: getVarTemp,
          context: { headers: this.headers },
          variables: { idEvaluacion: idEvaluacion, idVarTemp: idVarTemp }
        }).valueChanges
      );
      return data;
    } catch (err) {
      console.log(err);
      return null;
    }
  }
}
